import * as React from "react";
import { Form, Query } from "../../interfaces";
import {
  Typography,
  List,
  ListItem,
  Grid,
  Box,
  Button,
  IconButton,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { bankFailuresFormSelectChoices } from "../../refdata/constants";
import ClearReset from "../../containers/ClearReset";

import SelectFilterField from "../../containers/SelectFilterField";
import { ArrowLeftIcon, ArrowRightIcon } from "@mui/x-date-pickers";
import { yearChoice } from "../../common/services/Utilities";
import Sections from "../Sections";

export interface BankFailuresSearchForm extends Form {
  selectedReport: string;
  displayFields: string[];
  appStateDisplayFields: any;
}
const useStyles = makeStyles(theme => ({
  panelHeader: {
    padding: 0,
  },
  listDisplay: {
    "marginBottom": "1rem",
    "& li#select-report-li": {
      "paddingBottom": 0,
      "& .MuiFormControl-marginDense": {
        marginBottom: 0,
      },
      "& div.MuiGrid-item": {
        padding: 0,
      },
    },
    "& li#locations-li": {
      paddingBottom: 0,
    },
    "& li": {
      display: "block",
    },
  },
  hidePanelButton: {
    "marginTop": 0,
    "width": "100%",
    "textTransform": "capitalize",
    "fontSize": 14,
    "fontWeight": 400,
    "color": theme.palette.primary.main,
    "borderBottomRightRadius": 0,
    "borderBottomLeftRadius": 0,
    "transition": "none",
    "borderBottom": "1px solid #acc8df",
    "&:hover, &:focus": {
      color: "#ffffff",
      backgroundColor: theme.palette.primary.main,
    },
    "&:active": {
      color: theme.palette.primary.main,
      backgroundColor: "#ffffff",
    },
  },
  showPanelButton: {
    "borderRadius": 4,
    "backgroundColor": theme.palette.secondary.main,
    "color": theme.palette.primary.main,
    "border": "1px solid #acc8df",
    "transition": "none",
    "height": 40,
    "width": 40,
    "padding": 0,
    "&:hover, &:focus": {
      color: "#ffffff",
      backgroundColor: theme.palette.primary.main,
    },
    "&:active": {
      color: theme.palette.primary.main,
      backgroundColor: "#ffffff",
    },
  },
}));
export default function FailuresSearchForm({
  appState,
  searchPanelExpand,
  setSearchPanelExpand,
}: {
  appState: Query;
  searchPanelExpand: boolean;
  setSearchPanelExpand: (updatedSearchPanelExpand: boolean) => void;
}) {
  const classes = useStyles();

  const yearRange = yearChoice(1934, new Date().getFullYear());

  const handleCollapse = () => {
    setSearchPanelExpand(!searchPanelExpand);
  };

  const FailuresDataList = searchPanelExpand ? (
    <>
      <Typography style={{ padding: 0, marginBottom: "1rem" }} variant="h2">
        Report Parameters
      </Typography>
      <Box
        id="search-form-box-top"
        bgcolor="secondary.main"
        style={{ borderTopRightRadius: 5, borderTopLeftRadius: 5 }}
        justifyContent="center"
        border={"1px solid #ACC8DF"}
      >
        <Button
          id="hide-search-panel-button"
          className={classes.hidePanelButton}
          onClick={handleCollapse}
          startIcon={<ArrowLeftIcon />}
        >
          Hide search panel
        </Button>
        <List className={classes.listDisplay}>
          <ListItem id="select-report-li">
            <SelectFilterField
              uiValue={true}
              currentValue={appState.ui.aggReport}
              fieldName="aggReport"
              label={"Type of Report"}
              options={bankFailuresFormSelectChoices}
              noEmptyVal
            />
          </ListItem>
          <ListItem id="start-end-date-li">
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <SelectFilterField
                  currentValue={appState.filters.startDate.toString()}
                  fieldName="startDate"
                  label={"Start Year"}
                  options={yearRange}
                  noEmptyVal
                />
              </Grid>
              <Grid item xs={6}>
                <SelectFilterField
                  currentValue={
                    appState.filters.endDate?.toString() ||
                    new Date().getFullYear()
                  }
                  fieldName="endDate"
                  label={"End Year"}
                  options={yearRange}
                  noEmptyVal
                />
              </Grid>
            </Grid>
          </ListItem>
          <ListItem id="locations-li">
            <Sections
              selectedReport={appState.ui.aggReport}
              appState={appState}
              filterField="selectedStatesAlpha"
              displayFields={
                appState.filters.selectedStatesAlpha
                  ? appState.filters.selectedStatesAlpha
                  : [undefined]
              }
              popupWidth="large"
            />
          </ListItem>
        </List>
      </Box>
      <Box
        boxShadow="inset 0px 5px 5px -5px #1A4480"
        bgcolor="#ecf3fb"
        border={1}
        marginTop={-1}
        justifyContent="center"
        borderColor="#ACC8DF"
        style={{ borderBottomRightRadius: 5, borderBottomLeftRadius: 5 }}
        paddingTop={1}
        paddingBottom={0.5}
      >
        <ListItem>
          <Typography variant="h4" style={{ paddingBottom: 0 }}>
            Modify Report Filters
          </Typography>
        </ListItem>
        <ListItem id="clear-reset-li">
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <ClearReset
                aggReport
                buttonLabel="Default Filters"
                report={appState.ui.aggReport}
                buttonType="reset"
              />
            </Grid>

            <Grid item xs={6}>
              <ClearReset
                aggReport
                buttonLabel="Clear Filters"
                buttonType="clear"
              />
            </Grid>
          </Grid>
        </ListItem>
        <ListItem id="insurance-funds-filters-li">
          <Sections
            appState={appState}
            displayFields={
              appState.filters.selectedInsuranceFunds
                ? appState.filters.selectedInsuranceFunds
                : [undefined]
            }
            filterField="selectedInsuranceFunds"
            selectedReport={appState.ui.aggReport}
          />
        </ListItem>
        <ListItem id="charter-types-filters-li">
          <Sections
            appState={appState}
            displayFields={
              appState.filters.selectedCharterTypes
                ? appState.filters.selectedCharterTypes
                : [undefined]
            }
            filterField="selectedCharterTypes"
            selectedReport={appState.ui.aggReport}
          />
        </ListItem>
        <ListItem id="transaction-types-filters-li">
          <Sections
            appState={appState}
            displayFields={
              appState.filters.selectedTransactionTypes
                ? appState.filters.selectedTransactionTypes
                : [undefined]
            }
            filterField="selectedTransactionTypes"
            selectedReport={appState.ui.aggReport}
          />
        </ListItem>
      </Box>
    </>
  ) : (
    <IconButton
      id="show-search-panel-button"
      aria-label="Show Search Panel"
      title="Show Search Panel"
      className={classes.showPanelButton}
      onClick={handleCollapse}
      size="large"
    >
      <ArrowRightIcon />
    </IconButton>
  );

  return FailuresDataList;
}
